import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import { Button, Typography } from '@material-ui/core';
import useStyles from './styles';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import DividerWithText from '../DividerWithText/DividerWithText';
import CommodityProfiles from '../ProfilesTabs/VerticalTabs';
import AddIcon from '@material-ui/icons/Add';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { getCommodityProfiles } from '../../redux/actions/commodityGroupsActions';
import CommodityTranslationsModal from '../CommodityTranslations/CommodityTranslationsModal';
import { useTranslation } from 'react-i18next';

/**
 * Dialog with detailed information of the commodity selected
 * @param open
 * @param handleOpen
 * @param handleClose
 * @param OpenCommodityTranslation
 * @param handleCloseTranslationModal
 * @param CommoditySelected
 * @returns Commodity Dialog component
 */
export default function CommodityDetailsDialog({
  open,
  handleOpen,
  handleClose,
  OpenCommodityTranslation,
  handleCloseTranslationModal,
  CommoditySelected,
}) {
  const [addMode, setAddMode] = useState(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const { t } = useTranslation();
  const classes = useStyles();
  const selectedBrand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const dispatch: any = useDispatch();
  const { commodity } = useSelector(
    (state: RootStateOrAny) => state.CommodityGroupsReducer,
  );
  const { thisUser } = useSelector(
    (state: RootStateOrAny) => state.UserReducer,
  );
  const { commodityTranslations } = useSelector(
    (state: RootStateOrAny) => state.CommodityGroupsReducer,
  );

  /**
   * Dialog header
   * @param props
   * @returns Dialog Title component
   */
  const DialogTitle = props => {
    const { children, classes, onClose, ...other } = props;
    return (
      <MuiDialogTitle disableTypography {...other}>
        <Typography className={classes.title}>{children}</Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </MuiDialogTitle>
    );
  };

  const handleAddProfile = () => {
    setAddMode(true);
  };

  const closeDialog = () => {
    setAddMode(false);
    setEditMode(false);
    handleClose();
  };

  useEffect(() => {
    if (commodity.commoditySpecNumber) {
      dispatch(
        getCommodityProfiles(commodity.commoditySpecNumber, {
          brand: selectedBrand,
        }),
      );
    }
  }, [dispatch, commodity.commoditySpecNumber]);

  return (
    <React.Fragment>
      <Dialog
        fullWidth
        maxWidth="md"
        open={open}
        onClose={handleOpen}
        aria-labelledby="max-width-dialog-title"
        style={{ height: '100%' }}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
      >
        <DialogTitle
          id="max-width-dialog-title"
          classes={classes}
          onClose={closeDialog}
        >
          {commodity.commodity}
        </DialogTitle>
        <Divider />
        <DialogContent className={classes.root}>
          <Grid container spacing={0}>
            <Grid item lg={8}>
              <Grid container>
                <Grid item lg={8}>
                  <Typography className={classes.subtitle} gutterBottom>
                    {t('commodities.commodityInfo')}
                  </Typography>
                  <Grid container>
                    <Grid item lg={12}>
                      <table>
                        <tbody>
                          <tr>
                            <td style={{ width: 200 }}>
                              <Typography align="right">
                                {t('commodities.commodityVersion')}
                              </Typography>
                            </td>
                            <td>
                              <Typography align="left" className={classes.info}>
                                {commodity.commodityVersion}
                              </Typography>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ width: 200, verticalAlign: 'top' }}>
                              <Typography align="right">
                                {t('commodities.commoditySupplier')}
                              </Typography>
                            </td>
                            <td>
                              <Typography align="left" className={classes.info}>
                                {commodity.commoditySupplier}
                              </Typography>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ width: 200 }}>
                              <Typography align="right">
                                {t('commodities.SPEQNumber')}
                              </Typography>
                            </td>
                            <td>
                              <Typography align="left" className={classes.info}>
                                {commodity.commoditySpecNumber}
                              </Typography>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </Grid>
                  </Grid>

                  {commodity?.productGroups?.length > 0 && (
                    <Typography className={classes.subtitle} gutterBottom>
                      {t('commodities.associatedProductGroups')}
                    </Typography>
                  )}
                  {commodity?.productGroups?.map((productGroup, index) => (
                    <Typography key={index}>{productGroup.product}</Typography>
                  ))}
                </Grid>
                <Grid item lg={4} md={4} sm={4} xs={4}>
                  <Typography className={classes.subtitle}>
                    {t('app.common.availability')}
                  </Typography>
                  <Grid container>
                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div style={{ width: '100%' }}>
                        <table style={{ width: '100%' }}>
                          <tbody>
                            {commodity?.availability?.map((a, index) => (
                              <tr key={index}>
                                <td>
                                  <Typography align="right">{`${a.region}/`}</Typography>
                                </td>
                                <td>
                                  <Typography
                                    className={classes.info}
                                    align="left"
                                  >{` ${a.country}`}</Typography>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item lg={4} md={12} sm={12} xs={12}>
              <div className={classes.translationsContainer}>
                <div className={classes.containerAppBar}>
                  <Typography className={classes.containerTitle}>
                    {t('app.common.translations')} (
                    {
                      commodityTranslations?.filter(e => e.value !== null)
                        .length
                    }
                    )
                  </Typography>
                  <CommodityTranslationsModal
                    OpenCommodityTranslation={OpenCommodityTranslation}
                    handleClose={handleCloseTranslationModal}
                    CommoditySelected={CommoditySelected}
                  />
                </div>
                {commodityTranslations?.map(translation =>
                  translation.value === null ? (
                    ''
                  ) : (
                    <div key={translation.language.id}>
                      <DividerWithText mt={10}>
                        {t(translation.language.name?.toString())}
                      </DividerWithText>
                      <Typography className={classes.translationItem}>
                        {translation.value}
                      </Typography>{' '}
                    </div>
                  ),
                )}
              </div>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <div className={classes.holdingProfilesContainer}>
                <div className={classes.containerAppBar}>
                  <Typography className={classes.containerTitle}>
                    {t('commodities.holdingProfiles')}
                  </Typography>
                  {!addMode && (
                    <div className={classes.addMore}>
                      {thisUser.hasHoldingProfileCommodities && (
                        <Button
                          disabled={editMode}
                          onClick={handleAddProfile}
                          style={{
                            color: 'white',
                            textTransform: 'none',
                          }}
                          endIcon={<AddIcon />}
                        >
                          {t('equipment.addMore')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <CommodityProfiles
                    setAddMode={setAddMode}
                    addMode={addMode}
                    setEditMode={setEditMode}
                    editMode={editMode}
                  />
                </div>
              </div>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}
